export class UserUiSettings {
  dateFormat?: string;
  timeFormat?: string;
  dateTimeFormat?: string;
  default_theme?: string;
  primaryLanguage?: string;
  timeZone?: string;
}
export const AvailableDateFormats = {
  'YYYY-MM-DD': 'YYYY-MM-DD',
  'DD-MM-YYYY': 'DD-MM-YYYY',
  'MM-DD-YYYY': 'MM-DD-YYYY',
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'YYYY/MM/DD': 'YYYY/MM/DD',
  'DD/MM/YYYY': 'DD/MM/YYYY',
  'Day, DD Month YYYY': 'dddd, DD MMMM Y',
  'DD Month YYYY': 'DD MMMM Y',
};

/**
 * Mapping of the application's moment-style date formats (AvailableDateFormats)
 * to PrimeNG Calendar (date picker) compatible formats.
 *
 * PrimeNG `dateFormat` tokens differ from moment/dayjs:
 *  - DD (day two-digit) -> dd
 *  - MM (month two-digit) -> mm
 *  - YYYY (four-digit year) -> yy
 *  - Separators are preserved.
 *  - Complex tokens like `dddd` (weekday) and `MMMM` (full month name) are NOT supported
 *    by the simple Calendar formatting, so those entries are omitted or mapped to a
 *    simplified numeric representation.
 *
 * NOTE: If you need localized month or weekday names in display, prefer a custom
 *       header or use Angular DatePipe outside the picker rather than relying on `dateFormat`.
 */
export const AvailableDateFormatsPrimeNg: Record<string, string> = {
  // Basic numeric formats
  [AvailableDateFormats['YYYY-MM-DD']]: 'yy-mm-dd',
  [AvailableDateFormats['DD-MM-YYYY']]: 'dd-mm-yy',
  [AvailableDateFormats['MM-DD-YYYY']]: 'mm-dd-yy',
  [AvailableDateFormats['MM/DD/YYYY']]: 'mm/dd/yy',
  [AvailableDateFormats['YYYY/MM/DD']]: 'yy/mm/dd',
  [AvailableDateFormats['DD/MM/YYYY']]: 'dd/mm/yy',
  // Unsupported verbose formats mapped to a simplified numeric fallback:
  // 'Day, DD Month YYYY' -> fallback to numeric day-month-year
  [AvailableDateFormats['Day, DD Month YYYY']]: 'dd-mm-yy',
  // 'DD Month YYYY' -> fallback numeric
  [AvailableDateFormats['DD Month YYYY']]: 'dd-mm-yy',
};

export const AvailableTimeFormats = {
  'HH:MM:SS': 'HH:mm:ss',
  'HH:MM': 'HH:mm',
  'hh:mm Am/pm': 'hh:mm a',
  'hh:mm:ss Am/pm': 'hh:mm:ss a',
};

export interface TimeFormatConfig {
  is24HourFormat: boolean;
  showSeconds: boolean;
}

export const AvailableTimeFormatsPrimeNg: Record<string, TimeFormatConfig> = {
  [AvailableTimeFormats['HH:MM:SS']]: {
    showSeconds: true,
    is24HourFormat: true,
  },
  [AvailableTimeFormats['HH:MM']]: { showSeconds: false, is24HourFormat: true },
  [AvailableTimeFormats['hh:mm Am/pm']]: {
    showSeconds: false,
    is24HourFormat: false,
  },
  [AvailableTimeFormats['hh:mm:ss Am/pm']]: {
    showSeconds: true,
    is24HourFormat: false,
  },
};

export const AvailableDateTimeFormats = {
  'YYYY-MM-DDTHH:MM:SSZ': 'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:MM:SS+00:00': 'YYYY-MM-DD HH:mm:ss ZZZZZ',
  'DD/MM/YYYY HH:MM': 'DD/MM/YYYY HH:mm',
  'MM/DD/YYYY HH:MM': 'MM/DD/YYYY HH:mm',
  'YYYY-MM-DD HH:MM': 'YYYY-MM-DD HH:mm',
  'MM/DD/YYYY hh:mm A': 'MM/DD/YYYY hh:mm a',
  'DD/MM/YYYY HH:mm': 'DD/MM/YYYY HH:mm',
  'YYYY/MM/DD HH:mm': 'YYYY/MM/DD HH:mm',
  'Day, DD Month YYYY HH:MM': 'dddd, DD MMMM YYYY HH:mm',
  'DD Month YYYY, HH:MM': 'DD MMMM YYYY, HH:mm',
  'YYYY - 4-digit year': 'YYYY',
  'YY - 2-digit year': 'YY',
  'MM - 2-digit month': 'MM',
  'DD - 2-digit day': 'DD',
};

export const AvailableDateTimeFormatsPrimeNg: Record<
  string,
  {
    date: string | null;
    time: TimeFormatConfig | null;
  }
> = {
  [AvailableDateTimeFormats['YYYY-MM-DDTHH:MM:SSZ']]: {
    date: 'yy-mm-dd',
    time: {
      showSeconds: true,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['YYYY-MM-DDTHH:MM:SS+00:00']]: {
    date: 'yy-mm-dd',
    time: {
      showSeconds: true,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['DD/MM/YYYY HH:MM']]: {
    date: 'dd/mm/yy',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['MM/DD/YYYY HH:MM']]: {
    date: 'mm/dd/yy',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['YYYY-MM-DD HH:MM']]: {
    date: 'yy-mm-dd',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['MM/DD/YYYY hh:mm A']]: {
    date: 'mm/dd/yy',
    time: {
      showSeconds: false,
      is24HourFormat: false,
    },
  },
  [AvailableDateTimeFormats['DD/MM/YYYY HH:mm']]: {
    date: 'dd/mm/yy',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['YYYY/MM/DD HH:mm']]: {
    date: 'yy/mm/dd',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['Day, DD Month YYYY HH:MM']]: {
    date: 'dd-mm-yy',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['DD Month YYYY, HH:MM']]: {
    date: 'dd-mm-yy',
    time: {
      showSeconds: false,
      is24HourFormat: true,
    },
  },
  [AvailableDateTimeFormats['YYYY - 4-digit year']]: {
    date: 'yy',
    time: null,
  },
  [AvailableDateTimeFormats['YY - 2-digit year']]: {
    date: 'yy',
    time: null,
  },
  [AvailableDateTimeFormats['MM - 2-digit month']]: {
    date: 'mm',
    time: null,
  },
  [AvailableDateTimeFormats['DD - 2-digit day']]: {
    date: 'dd',
    time: null,
  },
};
